var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect } from "react";
// UTILS
import { clearAuthToken, clearRemoteAuthTokenSilently, getRemoteAuthTokenSilently, setAuthToken, tokenPassesValidation } from "./tokenUtils";
import { log } from "./logs";
import { EAuth } from "@timeedit/types/lib/enums";
export const useAuthentication = ({ authConfigId, authUrl, appId, customerSignature, setAuthState, appSpecificLoginFn, appSpecificLogoutFn, fallbackAuthUrl, enableLogs = false, logoutFromAllTabs }) => {
    const redirectToAuth = useCallback(() => {
        const fullPath = window.location.pathname + window.location.search;
        const params = {
            product: appId,
            context: encodeURIComponent(fullPath)
        };
        if (authConfigId) {
            params.authConfigId = authConfigId;
        }
        if (customerSignature) {
            params.customerSignature = customerSignature;
        }
        const pathQuery = new URLSearchParams(params).toString();
        const deepLink = `${authUrl}/deep-link?${pathQuery}`;
        log(enableLogs, `Redirecting to: ${deepLink}`);
        window.location.replace(deepLink);
    }, [appId, authConfigId, authUrl, customerSignature, enableLogs]);
    // if user click logout, redirect to origin auth page
    // if token is expired, redirect to deeplink
    const logout = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (clearRemoteAuthToken = true) {
        setAuthState(EAuth.PENDING);
        clearAuthToken();
        appSpecificLogoutFn === null || appSpecificLogoutFn === void 0 ? void 0 : appSpecificLogoutFn();
        if (clearRemoteAuthToken) {
            yield clearRemoteAuthTokenSilently(authUrl, enableLogs);
            if (fallbackAuthUrl) {
                yield clearRemoteAuthTokenSilently(fallbackAuthUrl, enableLogs);
            }
        }
        setAuthState(EAuth.NOT_AUTHENTICATED);
        redirectToAuth();
    }), [appSpecificLogoutFn, redirectToAuth, setAuthState]);
    useEffect(() => {
        if (logoutFromAllTabs) {
            const handleStorageChange = () => {
                const token = localStorage.getItem("te_auth_token");
                if (!token) {
                    logout();
                }
            };
            window.addEventListener("storage", handleStorageChange);
            return () => {
                window.removeEventListener("storage", handleStorageChange);
            };
        }
    }, [logoutFromAllTabs]);
    useEffect(() => {
        const validateAuthStatus = () => __awaiter(void 0, void 0, void 0, function* () {
            log(enableLogs, "Attempting to fetch token from auth service...");
            let token = yield getRemoteAuthTokenSilently(authUrl, enableLogs);
            if (!token && fallbackAuthUrl) {
                token = yield getRemoteAuthTokenSilently(fallbackAuthUrl, enableLogs);
            }
            if (!token) {
                log(enableLogs, "No token found when using iframe, authState = NOT_AUTHENTICATED");
                void logout(false);
            }
            else {
                log(enableLogs, "Found token...");
                try {
                    const { accessToken } = token;
                    log(enableLogs, "accessToken: ", accessToken);
                    const decodedToken = jwtDecode(accessToken);
                    log(enableLogs, "decodedToken: ", decodedToken);
                    log(enableLogs, "Token decoded, proceeding...");
                    if (tokenPassesValidation(decodedToken, appId)) {
                        log(enableLogs, "Token passes validation, setting in local storage and authState = AUTHENTICATED");
                        setAuthToken(token);
                        setAuthState(EAuth.AUTHENTICATED);
                        appSpecificLoginFn === null || appSpecificLoginFn === void 0 ? void 0 : appSpecificLoginFn(decodedToken);
                    }
                    else {
                        log(enableLogs, "Token did not pass validation, setting authState = NOT_AUTHENTICATED");
                        void logout();
                    }
                }
                catch (e) {
                    log(enableLogs, `Error when working with token: ${e}`);
                    void logout();
                    setAuthState(EAuth.NOT_AUTHENTICATED);
                }
            }
        });
        void validateAuthStatus();
    }, []);
    return logout;
};
